import React, { useState } from 'react';
import Slider from './Slider';
import Switch from './Switch';
import Button from './Button';
import { makeMultipleCalls } from '../hooks/api.ts';
import { useToast } from '../hooks/use-toast.tsx';

interface StartCampaignSliderProps {
  selectedRows: Campaign[];
  collectionName: string;
  onComplete: () => void;
}

interface CallRequest {
    name: string;
    phone_number: string;
    vehicle_name: string;
    city: string;
    service_center: string;
    due_date: string;
    odometer: string;
    license_plate: string;
    collection_name: string;
    calling_number: string;
    language: string;
}

interface Campaign {
    id?: string;
    _id?: string;
    [key: string]: any; // Allow for dynamic properties
}

const StartCampaignSlider = ({ selectedRows, collectionName, onComplete }: StartCampaignSliderProps) => {
  const [progress, setProgress] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const { toast } = useToast();

  const totalRows = selectedRows.length;

  const formatCallData = (rows: Campaign[]): { num_accounts: number; calls_per_account: number; payloads: CallRequest[] } => {
    const payloads = rows.map((row) => ({
      name: row.fullName || '',
      phone_number: row.primaryPhone || '',
      vehicle_name: `${row.make || ''} ${row.model || ''}`.trim(),
      city: row.serviceCentreCity || '',
      service_center: row.serviceCentreTitle || '',
      due_date: row.dueDate || '',
      odometer: String(row.odometerValue || ''),
      license_plate: row.licensePlate || '',
      collection_name: collectionName,
      calling_number: row.callingNumber || '',
      language: 'English', // Default language
    }));

    return {
      num_accounts: 3, // Default value
      calls_per_account: 3, // Default value
      payloads,
    };
  };

  const startCampaign = async () => {
    if (totalRows === 0) {
      toast({
        title: "No rows selected",
        description: "Please select at least one row to start the campaign.",
        variant: "destructive",
      });
      return;
    }

  try {
    setIsRunning(true);

    // Prepare call data for all selected rows
    const callData = formatCallData(selectedRows);
    console.log("Call data prepared:", callData);
    // Make all calls in a single API call
    const result = await makeMultipleCalls(callData);

    // Check the status from the API response
    if (result.status === "Processing started") {
      toast({
        title: "Campaign started successfully",
        description: `Processing ${result.total_payloads} calls`,// for ${result.accounts.length} accounts with ${result.calls_per_account} calls per account.`,
      });


    } else {
      toast({
        title: "Campaign failed to start",
        description: "There was an issue starting the campaign process.",
        variant: "destructive",
      });
    }

    setProgress(100);
    onComplete();
  } catch (error) {
    console.error("Error during campaign execution:", error);
      toast({
        title: "Error starting campaign",
        description: "There was an error starting the campaign. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium">Start Campaign</h3>
          <p className="text-sm text-gray-500">
            Collection: <span className="font-medium">{collectionName}</span>
          </p>
          <p className="text-sm text-gray-500">
            Selected: <span className="font-medium">{totalRows} rows</span>
          </p>
        </div>
      </div>
      
      <div className="mb-6">
        <Slider 
          value={[progress]} 
          max={100}
          step={1}
          disabled={true}
          className="w-full"
        />
        <div className="mt-1 text-right text-sm text-gray-500">{progress}% complete</div>
      </div>
      
      <div className="flex justify-end space-x-3">
        <Button
          variant="outline"
          disabled={isRunning}
          onClick={onComplete}
          className="px-4"
        >
          Cancel
        </Button>
        <Button 
          disabled={isRunning || totalRows === 0}
          onClick={startCampaign}
          className="px-4"
        >
          {isRunning ? "Running..." : "Start Campaign"}
        </Button>
      </div>
    </div>
  );
};

export default StartCampaignSlider;